import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AccountCampaign,
  AccountCampaignInlinePatch,
} from '../../types/accountCampaign';
import type { Rollout } from '../../types/rollout';
import { patchRow } from './api';
import { BuildViewSection } from './BuildViewSection';
import { seedPreviewLinksFromLabels } from './previewLinksSeed';

// P2b-3 Build View commit 5: §2.10 Execution — the only writable
// section on Build View. Visually distinct from the read-only sections
// above via an amber tint + heavier border, signalling "this is where
// the buyer makes changes."
//
// Edits go through the existing patchRow inline-patch endpoint:
// - Checkboxes save on toggle.
// - Preview Links saves on blur or Enter.
// - Notes saves on blur (binds to the shared rowNotes field per
//   round 1 Fix 4).
//
// Disabled across the board when the rollout is archived (matches the
// role grid's archived behaviour).

type Props = {
  rollout: Rollout;
  row: AccountCampaign;
  archived: boolean;
  platform: 'meta' | 'google';
};

export function BuildViewExecutionBlock({ rollout, row, archived, platform }: Props) {
  const qc = useQueryClient();
  const patchMut = useMutation({
    mutationFn: (patch: AccountCampaignInlinePatch) =>
      patchRow(rollout.id, row.id, patch),
    onSuccess: () => {
      // Invalidate every query key that might display this row so the
      // grid + Build View stay in sync after each edit.
      qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows'] });
      qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows', 'meta'] });
      qc.invalidateQueries({ queryKey: ['rollout', rollout.id, 'rows', 'google'] });
    },
  });
  const patch = (partial: AccountCampaignInlinePatch) => patchMut.mutate(partial);

  return (
    <section
      id="bv-execution"
      className="rounded-md border-2 border-amber-200 bg-amber-50/30 p-5"
    >
      <header className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-xs uppercase tracking-wide font-semibold text-amber-700">
          Execution
        </h2>
        {archived && (
          <span className="text-[10px] uppercase tracking-wide text-slate-400">
            Read-only (archived)
          </span>
        )}
      </header>

      <div className="space-y-4">
        {platform === 'meta' ? (
          <MetaCheckboxes row={row} archived={archived} onPatch={patch} />
        ) : (
          <GoogleCheckboxes row={row} archived={archived} onPatch={patch} />
        )}

        <PreviewLinksField
          value={
            platform === 'meta' ? row.metaPreviewLinks : row.googlePreviewLinks
          }
          // Item 5: Meta-only scaffold from the row's asset labels —
          // shown only when there are no Preview Links yet (the field
          // ignores the seed when `value` is non-empty), never clobbers
          // authored content.
          seed={
            platform === 'meta'
              ? seedPreviewLinksFromLabels(row.metaAssetLabels ?? [])
              : undefined
          }
          archived={archived}
          onCommit={(next) =>
            patch(
              platform === 'meta'
                ? { metaPreviewLinks: next }
                : { googlePreviewLinks: next },
            )
          }
        />

        <NotesField
          value={row.rowNotes}
          archived={archived}
          onCommit={(next) => patch({ rowNotes: next })}
        />
      </div>
    </section>
  );
}

// ---------- internals ----------

function MetaCheckboxes({
  row,
  archived,
  onPatch,
}: {
  row: AccountCampaign;
  archived: boolean;
  onPatch: (patch: AccountCampaignInlinePatch) => void;
}) {
  // Local Built gated on localAssetsUrl — matches the master grid +
  // role grid logic. With no URL there's nothing to build, so the
  // checkbox is suppressed in favour of an em-dash.
  const localAssetsConfigured =
    typeof row.localAssetsUrl === 'string' && row.localAssetsUrl.length > 0;
  // Organic Built mirrors Local Built exactly — same metaOrganicBuilt
  // field the master grid + role grid toggle, gated on organicAssetsUrl
  // being configured. Added here so AMs can tick it without bouncing back
  // to the main rollout screen.
  const organicAssetsConfigured =
    typeof row.organicAssetsUrl === 'string' && row.organicAssetsUrl.length > 0;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      <Checkbox
        label="Main Built"
        checked={row.metaBuilt}
        disabled={archived}
        onChange={(v) => onPatch({ metaBuilt: v })}
      />
      {localAssetsConfigured ? (
        <Checkbox
          label="Local Built"
          checked={row.metaLocalBuilt}
          disabled={archived}
          onChange={(v) => onPatch({ metaLocalBuilt: v })}
        />
      ) : (
        <DisabledCheckboxPlaceholder
          label="Local Built"
          tooltip="Local assets not configured"
        />
      )}
      {organicAssetsConfigured ? (
        <Checkbox
          label="Organic Built"
          checked={row.metaOrganicBuilt}
          disabled={archived}
          onChange={(v) => onPatch({ metaOrganicBuilt: v })}
        />
      ) : (
        <DisabledCheckboxPlaceholder
          label="Organic Built"
          tooltip="Organic assets not configured"
        />
      )}
      <Checkbox
        label="Scheduled/Live"
        checked={row.metaTurnedOn}
        disabled={archived}
        onChange={(v) => onPatch({ metaTurnedOn: v })}
      />
      <Checkbox
        label="Post-Check"
        checked={row.metaPostRolloutCheck}
        disabled={archived}
        onChange={(v) => onPatch({ metaPostRolloutCheck: v })}
      />
    </div>
  );
}

function GoogleCheckboxes({
  row,
  archived,
  onPatch,
}: {
  row: AccountCampaign;
  archived: boolean;
  onPatch: (patch: AccountCampaignInlinePatch) => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <Checkbox
        label="Main Built"
        checked={row.googleBuilt}
        disabled={archived}
        onChange={(v) => onPatch({ googleBuilt: v })}
      />
      <Checkbox
        label="Scheduled"
        checked={row.googleScheduled}
        disabled={archived}
        onChange={(v) => onPatch({ googleScheduled: v })}
      />
      <Checkbox
        label="Budget Updated"
        checked={row.googlePostRolloutCheck}
        disabled={archived}
        onChange={(v) => onPatch({ googlePostRolloutCheck: v })}
      />
    </div>
  );
}

function Checkbox({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-slate-300 text-slate-900 focus:ring-slate-400"
      />
      <span className={disabled ? 'text-slate-400' : 'text-slate-800'}>{label}</span>
    </label>
  );
}

function DisabledCheckboxPlaceholder({
  label,
  tooltip,
}: {
  label: string;
  tooltip: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-2 text-sm text-slate-400"
      title={tooltip}
    >
      <span aria-hidden className="inline-block w-3 h-3" />
      {label} <span className="text-xs">—</span>
    </span>
  );
}

function PreviewLinksField({
  value,
  seed,
  archived,
  onCommit,
}: {
  value: string | null;
  // Item 5: a hint used as the textarea's initial content ONLY when
  // there are no saved Preview Links. Authored content always wins.
  seed?: string;
  archived: boolean;
  onCommit: (next: string | null) => void;
}) {
  // Round-3 polish Fix 1: Build View buyers paste 4-5 preview URLs at
  // a time (one per ad variation), so this is a textarea, not the
  // single-line input the role grids use. Same save-on-blur semantics
  // as the Notes field below; Escape reverts. The Enter-blurs handler
  // is dropped — newlines are valid content here.
  //
  // Local mirror so external refetches don't clobber an in-progress
  // edit. Resync on `value` change when the textarea isn't focused.
  // Saved content wins; otherwise fall back to the seed scaffold (Item
  // 5) so an external refetch that leaves value empty doesn't wipe it.
  const baseline = value && value.length > 0 ? value : (seed ?? '');
  const [draft, setDraft] = useState<string>(baseline);
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) {
      setDraft(value && value.length > 0 ? value : (seed ?? ''));
    }
  }, [value, seed]);

  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-slate-500 block mb-1">
        Preview links
      </span>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={() => {
          focused.current = false;
          const next = draft.trim().length === 0 ? null : draft;
          if (next !== (value ?? null)) onCommit(next);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value ?? '');
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        readOnly={archived}
        rows={4}
        placeholder={archived ? '' : 'Paste preview URLs, one per line'}
        aria-label="Preview links"
        className="w-full text-sm rounded border border-slate-300 px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y"
      />
    </label>
  );
}

function NotesField({
  value,
  archived,
  onCommit,
}: {
  value: string | null;
  archived: boolean;
  onCommit: (next: string | null) => void;
}) {
  const [draft, setDraft] = useState<string>(value ?? '');
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setDraft(value ?? '');
  }, [value]);

  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-slate-500 block mb-1">
        Notes
      </span>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => {
          focused.current = true;
        }}
        onBlur={() => {
          focused.current = false;
          const next = draft.trim().length === 0 ? null : draft;
          if (next !== (value ?? null)) onCommit(next);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setDraft(value ?? '');
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
        readOnly={archived}
        rows={4}
        placeholder={archived ? '' : 'Notes for the buyer or AM…'}
        aria-label="Notes"
        className="w-full text-sm rounded border border-slate-300 px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y"
      />
    </label>
  );
}
